import React, { Component } from "react";
import styles from "./pearlDoser.module.css";
import DoserGraph from "./DoserGraph";
import {senseIds} from '../C18AiUtils.js'
import {cl} from '../../../components/utils/utils';

/** --- Utilities (not exported) --- */

/** Local day minutes only — do not use timezone offset (tzo). */
function formatDayMinutesToAmPm(mins) {
  if (mins == null || mins === "" || Number.isNaN(Number(mins))) return "—";
  const m = Math.floor(Number(mins)) % (24 * 60);
  const h24 = Math.floor(m / 60);
  const mm = m % 60;
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

/** EC / pH numeric display — 2 decimals */
function fmt2(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
}

/** Flow & injector-style numerics — 1 decimal */
function fmt1(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(1);
}

function formatDeviation(cur, sp) {
  if (cur == null || sp == null || Number.isNaN(Number(cur)) || Number.isNaN(Number(sp))) {
    return "—";
  }
  const v = Number(cur) - Number(sp);
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;
}

function ecPhStatus(cur, lo, hi) {
  if (lo != null && cur != null && cur < lo) {
    return { text: "Lockout Low", error:true };
  }
  if (hi != null && cur != null && cur > hi) {
    return { text: "Lockout High", error:true };
  }
  return { text: "Within range", error:false };
}

function flowStatus(curFlow, minFlow, maxFlow) {
  if (minFlow != null && maxFlow != null && Number(minFlow) >= Number(maxFlow)) {
    return "Normal";
  }
  if (minFlow != null && curFlow != null && curFlow <= minFlow) {
    return "Low Flow";
  }
  if (maxFlow != null && curFlow != null && curFlow >= maxFlow) {
    return "High Flow";
  }
  return "Normal";
}

function concForInjector(curRecipe, injIndex) {
  if (!curRecipe || !Array.isArray(curRecipe.nutes)) return "";
  const row = curRecipe.nutes.find((n) => n.inj === injIndex);
  if (!row || row.conc == null) return "";
  return String(row.conc);
}

function grValuesToGraphData(grValues) {
  return (grValues || [])
    .filter(
      (d) =>
        d &&
        typeof d.ts === "number" &&
        typeof d.gr === "number" &&
        Number.isFinite(d.ts) &&
        Number.isFinite(d.gr) &&
        +d.gr!=1e4&&
        +d.gr!=1e6
    )
    .map((d) => ({ t: d.ts, v: d.gr }));
}

const cardBase = {
  border: "1px solid #333",
  borderRadius: 10,
  padding: 10,
  boxSizing: "border-box",
  minWidth: 0,
};

const topRowItem = {
  ...cardBase,
  flex: "1 1 180px",
  minWidth: "180px",
  maxWidth: "20%",
  padding: "14px 8px 8px 8px",
  position:"relative",
};

const detailItem = {
  ...cardBase,
  flex: "1 1 240px",
  maxWidth: "calc(25% - 8px)",
  marginBottom: 10,
};

const rowWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  marginBottom: 10,
  alignItems: "stretch",
};

const topRowWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginBottom: 10,
  alignItems: "stretch",
};

const labelTyp = { fontSize: "12px", color: "#666" };
const errorTyp = { color:"white", background:"red" };
const valueTyp = { fontSize: "22px", fontWeight: "bold", fontVariantNumeric: "tabular-nums" };
const subTyp = { fontSize: "14px", color: "#444", fontVariantNumeric: "tabular-nums" };
const cardTitleTyp = { fontSize: "14px", fontWeight: 700, color: "#222", marginBottom: 6 };

const btnTyp = { fontSize: "12px", padding: "4px 8px" };

const stackMb = { marginBottom: "6px" };

const nutrientEllipsis = {
  width: "120px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function colorBar(color) {
  return (
    <div
      style={{
        height: 10,
        background: color,
        borderTopLeftRadius:10,
        borderTopRightRadius:10,
        borderBottomLeftRadius:0,
        borderBottomRightRadius:0,
        marginBottom: 6,
        position:"absolute",
        width:"100%",
        top:0,
        left:0
      }}
    />
  );
}

const volUnit=["L","Gal","Gal"]
const concUnit=["L/ml","Gal/oz","Gal/ml"]

function DetailLine({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: "6px",
      }}
    >
      <span style={{ opacity: 0.85 }}>{label}</span>
      <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

function measureRowWidth(rowEl) {
  if (!rowEl || !rowEl.children.length) return 0;
  const kids = rowEl.children;
  const first = kids[0];
  const last = kids[kids.length - 1];
  return last.offsetLeft + last.offsetWidth - first.offsetLeft;
}

/** --- Main dashboard --- */

export default class DoserDashboard extends Component {
  constructor(props) {
    super(props);
    this.topRowRef = React.createRef();
    this.state = { hoverInj: null, topRowWidth: null, };
  }

  componentDidMount() {
    this._syncTopRowWidth();
    window.addEventListener("resize", this._syncTopRowWidth);
  }

  componentDidUpdate() {
    this._syncTopRowWidth();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this._syncTopRowWidth);
  }

  _syncTopRowWidth = () => {
    const width = measureRowWidth(this.topRowRef.current);
    if (width > 0 && width !== this.state.topRowWidth) {
      this.setState({ topRowWidth: width });
    }
  };

  showGraphTitle=()=>{
    var selSensor=(k)=>{
      onChange({type:"selGraph",value:k})
    }
    let {units,selGraph,onChange}=this.props
//     cl(units)
    var style,str,unit
    let cols=Object.keys(senseIds).map((k,i)=>{
      if(k==selGraph){
        style={color:"black"}
        let unitId=senseIds[k].unit
        unit=units[unitId]
        if(unitId=="v"){unit+="/Min"}
        str=`${k} Trend${unit?` (${unit})`:""}`
      }else{
        style={color:"#4080C0",cursor:"pointer"}
        str=k
      }
      return (
        <td
          key={k}
          width="150"
          style={style}
          onClick={e=>{selSensor(k)}}
        >{str}
        </td>
      )
    })
    return(
      <table><tbody>
      <tr>{cols}</tr>
      </tbody></table>
    )
//     return <span>{`EC trend (mS)`}</span>
  }

  render() {
    const {
      systemStatus,
      onChange,
      curEc,
      spEc,
      locLoEc,
      locHiEc,
      calEc,
      grValues,
      tzo,
      curPh,
      spPh,
      locLoPh,
      locHiPh,
      calPh,
      curFlow,
      minFlow,
      maxFlow,
      todayFlow,
      weekFlow,
      injectors,
      curRecipe,
      kFactor,
      units,
      selGraph,
    } = this.props;
//     cl(grValues)

    const ecStat = ecPhStatus(curEc, locLoEc, locHiEc);
    const phStat = ecPhStatus(curPh, locLoPh, locHiPh);
    const flowStat = flowStatus(curFlow, minFlow, maxFlow);

    const devEc = formatDeviation(curEc, spEc);
    const devPh = formatDeviation(curPh, spPh);

    const recipeName =
      curRecipe && curRecipe.name ? curRecipe.name : "No Recipe";

    const { topRowWidth } = this.state;
    const graphPlotWidth = topRowWidth
      ? Math.max(0, topRowWidth - 22)
      : 600;
    const senseId=senseIds[selGraph]

//           <button
//             type="button"
//             style={btnTyp}
//             onClick={() => onChange({ type: "click", value: "stop" })}
//           >
//             Stop
//           </button>
//           <button
//             type="button"
//             style={{ ...btnTyp, marginLeft: 8 }}
//             onClick={() => onChange({ type: "click", value: "settings" })}
//           >
//             Settings
//           </button>
    return (
      <div id="/doserDash" className={styles.doserDashboard} style={{ position: "relative" }}>
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 2,
          }}
        >
        <button type="button"
        style={{padding:10,fontWeight:700,color:"white",background:"#FF0000"}}
        >STOP</button>
        </div>

        <div style={{ paddingRight: 140, marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700, ...stackMb }}>iDoser Dashboard&nbsp;<span
            style={{color:"#4080C0",cursor:"pointer"}}
            onClick={e=>{onChange({type:"click",value:"info"})}}
          >Info</span>
          </div>
          <div style={{ ...labelTyp, fontVariantNumeric: "tabular-nums" }}>
            System: {systemStatus != null ? String(systemStatus) : "—"}
          </div>
        </div>

        {/* Top row: 4 cards */}
        <div ref={this.topRowRef} style={topRowWrap}>
          <div id="/doserDash/ddEc" style={topRowItem}>
            {colorBar("#8B4513")}
            <div style={cardTitleTyp}>Electrical Conductivity</div>
            <div style={{ ...labelTyp, ...stackMb }}>Current</div>
            <div style={{ ...valueTyp, ...stackMb }}>{
              `${fmt2(curEc)} mS`
            }</div>
            <div style={{ ...labelTyp, ...stackMb }}>Status</div>
            <div style={{ ...subTyp, ...stackMb,
              ...(ecStat.error && errorTyp) }}>{ecStat.text}</div>
          </div>

          <div id="/doserDash/ddpH" style={topRowItem}>
            {colorBar("#e02020")}
            <div style={cardTitleTyp}>pH</div>
            <div style={{ ...labelTyp, ...stackMb }}>Current</div>
            <div style={{ ...valueTyp, ...stackMb }}>{fmt2(curPh)}</div>
            <div style={{ ...labelTyp, ...stackMb }}>Status</div>
            <div style={{ ...subTyp, ...stackMb,
              ...(phStat.error && errorTyp) }}>{phStat.text}</div>
          </div>

          <div id="/doserDash/ddFlow" style={topRowItem}>
            {colorBar("#007FFF")}
            <div style={cardTitleTyp}>Flow Rate</div>
            <div style={{ ...labelTyp, ...stackMb }}>Current</div>
            <div style={{ ...valueTyp, ...stackMb }}>{
              `${fmt1(curFlow)} ${units.v}/Min`

            }</div>
            <div style={{ ...labelTyp, ...stackMb }}>Status</div>
            <div style={subTyp}>{flowStat}</div>
          </div>

          <div id="/doserDash/ddVol" style={topRowItem}>
            {colorBar("#228B22")}
            <div style={cardTitleTyp}>Total Flow Today</div>
            <div style={{ ...labelTyp, ...stackMb }}>Today</div>
            <div style={{ ...valueTyp, ...stackMb }}>{
              `${fmt1(todayFlow)} ${units.v}`
            }</div>
          </div>
        </div>

        <div style={{ height: 10 }} />

        {/* Detail row */}
        <div style={{ ...rowWrap, marginBottom: 12 }}>
          <div id="/doserDash/ddEc" style={detailItem}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>EC Detail</div>
            <DetailLine label="Lockout low" value={fmt2(locLoEc)} />
            <DetailLine label="Lockout high" value={fmt2(locHiEc)} />
          </div>

          <div id="/doserDash/ddpH" style={detailItem}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>pH Detail</div>
            <DetailLine label="Lockout low" value={fmt2(locLoPh)} />
            <DetailLine label="Lockout high" value={fmt2(locHiPh)} />
          </div>

          <div id="/doserDash/ddFlow" style={detailItem}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Flow Detail</div>
            <DetailLine label="Instant rate" value={fmt1(curFlow)} />
            <DetailLine label="Today total" value={fmt1(todayFlow)} />
            <DetailLine label="Week total" value={fmt1(weekFlow)} />
            <DetailLine label="K-Factor" value={kFactor != null ? String(kFactor) : "—"} />
          </div>

          <div id="/doserDash/ddVol" style={detailItem}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Injection Cycle</div>
            <DetailLine label="Recipe" value={recipeName} />
            <DetailLine
              label="Start time"
              value={
                curRecipe && curRecipe.startTime != null
                  ? formatDayMinutesToAmPm(curRecipe.startTime)
                  : "—"
              }
            />
            <DetailLine
              label="End time"
              value={
                curRecipe && curRecipe.endTime != null
                  ? formatDayMinutesToAmPm(curRecipe.endTime)
                  : "—"
              }
            />
            <DetailLine
              label="Total flow"
              value={
                curRecipe && curRecipe.totalFlow != null ? fmt1(curRecipe.totalFlow) : "—"
              }
            />
          </div>
        </div>

        {/* Pump injectors */}
        <div id="/doserDash/ddPumpInj" style={{ ...cardBase, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Pump injectors</div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      width: "40px",
                    }}
                  >
                    Ch
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      width: "120px",
                    }}
                  >
                    Nutrient Name
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      borderBottom: "1px solid #ccc",
                      width: "80px",
                    }}
                  >
                    Conc
                  </th>
                </tr>
              </thead>
              <tbody>
                {(injectors || [])
                  .filter(inj=>inj.nute!="None")
                  .map((inj, idx) => (
                  <tr
                    key={idx}
                    onMouseEnter={() => this.setState({ hoverInj: idx })}
                    onMouseLeave={() => this.setState({ hoverInj: null })}
                    style={{
                      background:
                        this.state.hoverInj === idx
                          ? "#eef6ff"
                          : idx % 2
                            ? "#fafafa"
                            : "white",
                    }}
                  >
                    <td style={{ padding: "4px 4px 4px 0", width: "40px" }}>{idx + 1}</td>
                    <td
                      style={{
                        ...nutrientEllipsis,
                        padding: "4px 0",
                      }}
                    >
                      {inj.nute != null ? inj.nute : "—"}
                    </td>
                    <td style={{ padding: "4px 0", textAlign: "right", width: "80px" }}>
                      {concForInjector(curRecipe, idx)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* EC trend */}
        <div id="/doserDash/ddGraph" style={{ width: topRowWidth || "auto", maxWidth: "100%" }}>
          <DoserGraph
            title={this.showGraphTitle()}
            unit={units[senseId.unit]+((senseId.unit=="v")?"/m":"")}
            color={senseId.color}
            yDom={[senseId.min,senseId.max]}
            autoRange={selGraph==="Flow"}
            width={graphPlotWidth}
            height="200"
            data={grValuesToGraphData(grValues)}
            tzo={tzo}
          />
        </div>
      </div>
    );
//                     <td style={{ padding: "4px 0", textAlign: "right", width: "80px" }}>
//                       CPS: {inj.cps != null ? fmt1(inj.cps) : "—"}
//                     </td>
//                 <th
//                   style={{
//                     textAlign: "right",
//                     borderBottom: "1px solid #ccc",
//                     width: "80px",
//                   }}
//                 >
//                   CPS
//                 </th>
//             <div style={{ ...labelTyp, ...stackMb }}>Recipe</div>

//             <div
//               style={{
//                 ...subTyp,
//                 ...stackMb,
//                 ...(curRecipe
//                   ? { fontWeight: "bold", color: "#2e7d32" }
//                   : { color: "#888", fontWeight: "normal" }),
//               }}
//             >
//               {recipeName}
//             </div>
//             <div
//               style={{
//                 marginTop: 8,
//                 height: 60,
//                 maxHeight: 60,
//                 overflowY: "auto",
//                 fontSize: 11,
//                 borderTop: "1px solid #ddd",
//                 paddingTop: 4,
//                 boxSizing: "border-box",
//                 fontVariantNumeric: "tabular-nums",
//               }}
//             >
//               {(injectors || []).map((inj, i) => {
//                 const name = inj.nute != null ? inj.nute : `Injector ${i}`;
//                 const valStr =
//                   inj.todayInject != null && !Number.isNaN(Number(inj.todayInject))
//                     ? `${fmt1(inj.todayInject)}L`
//                     : "—";
//                 return (
//                   <div
//                     key={i}
//                     onMouseEnter={() => this.setState({ hoverInj: i })}
//                     onMouseLeave={() => this.setState({ hoverInj: null })}
//                     style={{
//                       display: "flex",
//                       justifyContent: "space-between",
//                       gap: 8,
//                       lineHeight: 1.35,
//                       marginBottom: 4,
//                       background: this.state.hoverInj === i ? "#eef6ff" : "transparent",
// //                       marginLeft: -2,
// //                       marginRight: -2,
//                       paddingLeft: 2,
//                       paddingRight: 2,
//                       borderRadius: 2,
//                     }}
//                   >
//                     <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
//                       {name}
//                     </span>
//                     <span style={{ flexShrink: 0 }}>{valStr}</span>
//                   </div>
//                 );
//               })}
//             </div>
  }
}
